import type { GetRequestsResponse } from "@nlc/shared-types";
import type { SortDescriptor } from "react-stately";

import { useQuery } from "@tanstack/react-query";

import { requestListKeys } from "@/hooks/queryKeys";
import { useUser } from "@/hooks/useUser";
import apiClient from "@/lib/api/client";

import type { RequestFilterType } from "../../types/filters";

import { getRequestTypes } from "../../utils/requestUtils";
import { mapSortColumnToBackend } from "../../utils/sortingUtils";

interface UseActiveRequestsParams {
  enabled?: boolean;
  filter?: RequestFilterType;
  limit?: number;
  page?: number;
  search?: string;
  sortColumn?: string;
  sortDescriptor?: SortDescriptor;
  sortDirection?: "asc" | "desc";
}

export function useActiveRequests(params: UseActiveRequestsParams = {}) {
  const { data: userData } = useUser();
  const projectId = userData?.currentProjectId;

  return useQuery({
    enabled: !!userData?.user && (params.enabled ?? true), // Only enabled when user is authenticated and explicitly enabled
    gcTime: 10 * 60 * 1000, // 10 minutes garbage collection
    queryFn: async () => {
      // Build query parameters
      const queryParams: Record<string, number | string | string[]> = {
        direction: params.sortDescriptor
          ? params.sortDescriptor.direction === "descending"
            ? "desc"
            : "asc"
          : params.sortDirection || "desc",
        // Explicitly request all needed fields for the active tab
        fields: [
          "id",
          "name",
          "request_type",
          "status",
          "created_by",
          "assignee",
          "brand",
          "created_at",
          "due_on",
          "design_type",
        ],
        limit: params.limit || 10, // Default pagination limit
        page: params.page || 1,
        skipCount: "false",
        sortBy: params.sortDescriptor
          ? mapSortColumnToBackend(String(params.sortDescriptor.column))
          : mapSortColumnToBackend(params.sortColumn || "createdAt"),
        tab: "active",
      };

      // Always get count from the data query, never from a skipped/cached count
      queryParams.skipCount = "false";

      // Add projectId for customer account filtering
      if (projectId) {
        queryParams.projectId = projectId;
      }

      // Add category filter if provided
      if (params.filter && params.filter !== "all") {
        const filterTypes = getRequestTypes(params.filter);

        queryParams.category = filterTypes;
      }

      // Add search if provided
      if (params.search) {
        queryParams.search = params.search;
      }

      return await apiClient
        .url("/requests/list")
        .query(queryParams)
        .get()
        .json<GetRequestsResponse>();
    },
    queryKey: [
      ...requestListKeys.all,
      "active",
      projectId,
      params.filter,
      params.limit,
      params.page,
      params.search,
      params.sortDescriptor?.column || params.sortColumn,
      params.sortDescriptor?.direction || params.sortDirection,
    ],
    refetchInterval: (data) =>
      data && document.visibilityState === "visible" ? 45 * 1000 : false,
    // Shorter stale time when sorting is active to ensure fresh data for sorting operations
    staleTime:
      params.sortDescriptor || params.sortColumn
        ? 1 * 60 * 1000
        : 3 * 60 * 1000, // 1 minute when sorting, 3 minutes otherwise
  });
}
